/**
 * BadgePanel.js - Student Gamification Badge & Points Panel
 * 
 * Displays:
 * - Current badge with visual icon
 * - Total points and breakdown (quiz, answer, review, exam, code, streak, session)
 * - Current & longest streak
 * - Progress bar to next badge
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL, apiFetch, isDemoMode } from '../config';

// Badge definitions with thresholds and display info
const BADGE_TIERS = [
  { name: 'NONE', minPoints: 0, icon: '🌱', color: '#9ca3af', label: 'No Badge' },
  { name: 'BRONZE', minPoints: 240, icon: '🥉', color: '#cd7f32', label: 'Bronze' },
  { name: 'SILVER', minPoints: 350, icon: '🥈', color: '#c0c0c0', label: 'Silver' },
  { name: 'GOLD', minPoints: 480, icon: '🥇', color: '#ffd700', label: 'Gold' },
  { name: 'PLATINUM', minPoints: 680, icon: '🪙', color: '#e5e4e2', label: 'Platinum' },
  { name: 'DIAMOND', minPoints: 960, icon: '💎', color: '#b9f2ff', label: 'Diamond' },
];

const getBadgeDisplay = (badgeLevel, totalPoints) => {
  // Calculate badge client-side from totalPoints (ignore backend badge_level which may be stale)
  if (totalPoints >= 960) {
    const level = Math.floor((totalPoints - 960) / 430) + 1;
    return { icon: '💎', color: '#b9f2ff', label: `Diamond ${level}`, tier: 5 + level };
  }
  // Find highest tier the student qualifies for
  for (let i = BADGE_TIERS.length - 1; i >= 0; i--) {
    if (totalPoints >= BADGE_TIERS[i].minPoints) {
      return { icon: BADGE_TIERS[i].icon, color: BADGE_TIERS[i].color, label: BADGE_TIERS[i].label, tier: i };
    }
  }
  return { icon: '🌱', color: '#9ca3af', label: 'No Badge', tier: 0 };
};

const getNextBadgeInfo = (totalPoints) => {
  if (totalPoints >= 960) {
    // Diamond repeating every 430
    const currentDiamond = Math.floor((totalPoints - 960) / 430) + 1;
    const nextThreshold = 960 + (currentDiamond * 430);
    return { nextBadge: `Diamond ${currentDiamond + 1}`, pointsNeeded: nextThreshold - totalPoints, nextThreshold };
  }
  for (let i = 1; i < BADGE_TIERS.length; i++) {
    if (totalPoints < BADGE_TIERS[i].minPoints) {
      return { nextBadge: BADGE_TIERS[i].label, pointsNeeded: BADGE_TIERS[i].minPoints - totalPoints, nextThreshold: BADGE_TIERS[i].minPoints };
    }
  }
  return { nextBadge: 'Diamond', pointsNeeded: 960 - totalPoints, nextThreshold: 960 };
};

const getProgressPercent = (totalPoints) => {
  if (totalPoints >= 960) {
    const currentDiamond = Math.floor((totalPoints - 960) / 430) + 1;
    const prevThreshold = 960 + ((currentDiamond - 1) * 430);
    const nextThreshold = 960 + (currentDiamond * 430);
    return ((totalPoints - prevThreshold) / (nextThreshold - prevThreshold)) * 100;
  }
  for (let i = 1; i < BADGE_TIERS.length; i++) {
    if (totalPoints < BADGE_TIERS[i].minPoints) {
      const prev = BADGE_TIERS[i - 1].minPoints;
      const next = BADGE_TIERS[i].minPoints;
      return ((totalPoints - prev) / (next - prev)) * 100;
    }
  }
  return 100;
};

const BadgePanel = ({ username, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDemo] = useState(isDemoMode());

  useEffect(() => {
    if (isDemo) { setLoading(false); return; }
    if (!username) return;
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/points/profile?username=${encodeURIComponent(username)}`);
        if (res.ok) {
          const data = await res.json();
          setProfile(data);
        } else {
          setError('Could not load points profile');
        }
      } catch (err) {
        console.error('BadgePanel fetch error:', err);
        setError('Could not connect to points server');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username]);

  const badgeDisplay = profile ? getBadgeDisplay(profile.badge_level, profile.total_points) : null;
  const nextBadge = profile ? getNextBadgeInfo(profile.total_points) : null;
  const progress = profile ? getProgressPercent(profile.total_points) : 0;

  const pointsBreakdown = profile ? [
    { label: 'Quiz', value: profile.quiz_points || 0, icon: '📝', color: '#58a6ff' },
    { label: 'Answer', value: profile.answer_points || 0, icon: '💬', color: '#7ee787' },
    { label: 'Review', value: profile.review_points || 0, icon: '📖', color: '#d2a8ff' },
    { label: 'Exam', value: profile.exam_points || 0, icon: '🎓', color: '#f0883e' },
    { label: 'Code', value: profile.struggle_points || 0, icon: '🧩', color: '#79c0ff' },
    { label: 'Streak', value: profile.streak_points || 0, icon: '🔥', color: '#f85149' },
    { label: 'Session', value: profile.time_points || 0, icon: '⏱️', color: '#f0e68c' },
  ] : [];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      zIndex: 10000,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }}>
      <div style={{
        backgroundColor: '#0d1117', border: '1.5px solid rgba(255, 215, 102, 0.4)', borderRadius: '14px',
        width: '640px', overflow: 'hidden',
        padding: '18px 22px', position: 'relative',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '10px', right: '12px',
          background: 'none', border: 'none', color: '#ffffff',
          fontSize: '22px', cursor: 'pointer', fontWeight: 'bold',
        }}
        onMouseOver={(e) => e.target.style.color = '#f85149'}
        onMouseOut={(e) => e.target.style.color = '#ffffff'}
        >✕</button>

        <h2 style={{ color: '#fffacd', textAlign: 'center', margin: '0 0 12px 0', fontSize: '26px' }}>
          🏆 Badge & Points
        </h2>

        {isDemo && (
          <div style={{ color: '#fffacd', padding: '16px 8px 24px', whiteSpace: 'pre-wrap', lineHeight: '1.7', fontSize: '14px' }}>
            {`🔒 Badge & Points — Locked in Demo Mode

With a real account you earn points every time you study, and they unlock badge tiers:

  🌱  No Badge   —  starting out
  🥉  Bronze     —  240 pts
  🥈  Silver     —  350 pts
  🥇  Gold       —  480 pts
  🪙  Platinum   —  680 pts
  💎  Diamond    —  960 pts  (and keeps levelling up every 430 pts)

Points are awarded for:
  📝  Quiz answers          💬  Asking the tutor
  📖  Reviewing past quizzes  🎓  Practice exams
  🧩  Code exercises         🔥  Daily login streaks
  ⏱️  Session time

Example: answer 5 quiz questions (+50 pts) + 10-minute session (+20 pts)
         + login 3 days in a row (+30 pts) = 100 pts toward Bronze.

✨ Sign up to start earning points and climbing the leaderboard!`}
          </div>
        )}

        {!isDemo && loading && (
          <div style={{ textAlign: 'center', color: '#8b949e', padding: '30px 0' }}>
            Loading...
          </div>
        )}

        {!isDemo && error && (
          <div style={{ textAlign: 'center', color: '#f85149', padding: '30px 0' }}>
            {error}
          </div>
        )}

        {!isDemo && profile && !loading && (
          <>
            {/* Badge + Streak row */}
            <div style={{
              display: 'flex', gap: '12px', marginBottom: '12px',
            }}>
              {/* Badge Display */}
              <div style={{
                flex: 1, textAlign: 'center', padding: '12px',
                background: 'linear-gradient(135deg, #1e2733 0%, #161b22 100%)',
                borderRadius: '10px', border: `1px solid ${badgeDisplay.color}40`,
              }}>
                <div style={{ fontSize: '42px', marginBottom: '2px' }}>
                  {badgeDisplay.label.startsWith('Diamond')
                    ? '💎'.repeat(Math.min(parseInt(badgeDisplay.label.split(' ')[1]) || 1, 5))
                    : badgeDisplay.icon}
                </div>
                <div style={{
                  fontSize: '16px', fontWeight: 'bold',
                  color: badgeDisplay.color, marginBottom: '2px',
                }}>
                  {badgeDisplay.label}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f5e6a3' }}>
                  {profile.total_points} pts
                </div>
              </div>

              {/* Streak Display */}
              <div style={{
                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px',
                background: '#1e2733', borderRadius: '10px',
                minWidth: '140px', textAlign: 'center',
              }}>
                <div>
                  <div style={{ fontSize: '13px' }}>🔥</div>
                  <div style={{ color: '#f85149', fontSize: '18px', fontWeight: 'bold' }}>
                    {profile.current_streak || 0}
                  </div>
                  <div style={{ color: '#eef1f5', fontSize: '10px' }}>Current Streak</div>
                </div>
                <div style={{ height: '1px', backgroundColor: '#30363d' }} />
                <div>
                  <div style={{ fontSize: '13px' }}>⭐</div>
                  <div style={{ color: '#f0e68c', fontSize: '18px', fontWeight: 'bold' }}>
                    {profile.longest_streak || 0}
                  </div>
                  <div style={{ color: '#eef1f5', fontSize: '10px' }}>Longest Streak</div>
                </div>
              </div>
            </div>

            {/* Progress to Next Badge */}
            {nextBadge && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: '11px', color: '#eef1f5', marginBottom: '4px',
                }}>
                  <span>Next: {nextBadge.nextBadge}</span>
                  <span>{nextBadge.pointsNeeded} pts to go</span>
                </div>
                <div style={{
                  height: '6px', backgroundColor: '#21262d',
                  borderRadius: '3px', overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', width: `${Math.min(progress, 100)}%`,
                    background: 'linear-gradient(90deg, #b2f5e8, #5aedc7, #00d4aa, #00b892, #007a5e)',
                    borderRadius: '3px', transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            )}

            {/* Points Breakdown */}
            <div style={{
              background: '#1e2733', borderRadius: '10px',
              border: '1px solid #30363d', padding: '14px 16px', marginBottom: '14px',
            }}>
              <div style={{ color: '#f5e6a3', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px' }}>
                Points Breakdown
              </div>
              {/* Top row: 4 items */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                {pointsBreakdown.slice(0, 4).map((item) => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 10px', backgroundColor: '#151c25',
                    borderRadius: '8px', border: '1px solid #30363d',
                  }}>
                    <span style={{ fontSize: '16px' }}>{item.icon}</span>
                    <div>
                      <div style={{ color: '#c9d1d9', fontSize: '11px' }}>{item.label}</div>
                      <div style={{ color: item.color, fontSize: '15px', fontWeight: 'bold' }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Bottom row: 3 items centered */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', maxWidth: '75%', margin: '0 auto' }}>
                {pointsBreakdown.slice(4).map((item) => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 10px', backgroundColor: '#151c25',
                    borderRadius: '8px', border: '1px solid #30363d',
                  }}>
                    <span style={{ fontSize: '16px' }}>{item.icon}</span>
                    <div>
                      <div style={{ color: '#c9d1d9', fontSize: '11px' }}>{item.label}</div>
                      <div style={{ color: item.color, fontSize: '15px', fontWeight: 'bold' }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How to Earn Points */}
            <div style={{
              padding: '14px 16px',
              background: '#1e2733', borderRadius: '10px',
              border: '1px solid #30363d',
            }}>
              <div style={{ color: '#f5e6a3', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px' }}>
                💡 How to Earn Points
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', fontSize: '12px', color: '#c9d1d9', lineHeight: '1.8' }}>
                <div>✅ Answer questions correctly</div>
                <div>📝 Pass quizzes</div>
                <div>📖 Review topics</div>
                <div>🎓 Ace practice exams</div>
                <div>🧩 Debug & run code</div>
                <div>⏱️ Study daily</div>
                <div>🔥 Keep your streak going!</div>
              </div>
              <div style={{ fontSize: '11px', color: '#8b949e', marginTop: '8px', fontStyle: 'italic' }}>
                Accuracy and consistency earn more than volume. Focus on understanding each topic.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BadgePanel;
